// Route map. Email deep links land on canonical URLs (/shift/:id,
// /schedule/:weekStart, /approvals) — the reason Callboard uses history
// routing instead of the house hash-routing. Heavy, rarely-first-paint areas
// (builder, reports, admin, print) are lazy chunks; employee surfaces ship in
// the main bundle.
import { createBrowserRouter, redirect } from 'react-router'
import { AppShell } from './shell/AppShell'
import { NotFound, RequireCapability } from './auth/RequireCapability'
import { todayLocal, weekKeyFor } from './lib/time'

import MyShiftsPage from './features/myShifts/MyShiftsPage'
import ShiftDetailPage from './features/myShifts/ShiftDetailPage'
import TeamPage from './features/team/TeamPage'
import OpenShiftsPage from './features/openShifts/OpenShiftsPage'
import RequestsPage from './features/requests/RequestsPage'
import AvailabilityPage from './features/availability/AvailabilityPage'
import ProfilePage from './features/profile/ProfilePage'

export const router = createBrowserRouter([
  // Chrome-free print routes (window.print → PDF).
  {
    path: '/print/week/:departmentId/:weekStart',
    lazy: async () => ({ Component: (await import('./features/printExport/PrintWeekPage')).default }),
  },
  {
    path: '/print/day/:departmentId/:date',
    lazy: async () => ({ Component: (await import('./features/printExport/PrintDayPage')).default }),
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <MyShiftsPage /> },
      { path: 'shift/:shiftId', element: <ShiftDetailPage /> },
      { path: 'team', element: <TeamPage /> },
      { path: 'open', element: <OpenShiftsPage /> },
      { path: 'requests', element: <RequestsPage /> },
      { path: 'availability', element: <AvailabilityPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'schedule', loader: () => redirect(`/schedule/${weekKeyFor(todayLocal())}`) },
      {
        path: 'schedule/:weekStart',
        lazy: async () => {
          const { default: SchedulePage } = await import('./features/schedule/SchedulePage')
          return {
            Component: () => (
              <RequireCapability capability="build_schedule">
                <SchedulePage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'templates',
        lazy: async () => {
          const { default: TemplatesPage } = await import('./features/schedule/TemplatesPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_templates">
                <TemplatesPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'approvals',
        lazy: async () => {
          const { default: ApprovalsPage } = await import('./features/approvals/ApprovalsPage')
          return {
            Component: () => (
              <RequireCapability capability="approve_requests">
                <ApprovalsPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'roster',
        lazy: async () => {
          const { default: RosterPage } = await import('./features/roster/RosterPage')
          return {
            Component: () => (
              <RequireCapability capability="build_schedule">
                <RosterPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'roster/:email',
        lazy: async () => {
          const { default: EmployeeDetailPage } = await import('./features/roster/EmployeeDetailPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_roster">
                <EmployeeDetailPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'credentials',
        lazy: async () => {
          const { default: CredentialBoardPage } = await import('./features/credentials/CredentialBoardPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_credentials">
                <CredentialBoardPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { default: SettingsPage } = await import('./features/settings/SettingsPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_department">
                <SettingsPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'reports',
        lazy: async () => {
          const { default: ReportsPage } = await import('./features/reports/ReportsPage')
          return {
            Component: () => (
              <RequireCapability capability="approve_requests">
                <ReportsPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'admin',
        lazy: async () => {
          const { default: AdminDepartmentsPage } = await import('./features/admin/AdminDepartmentsPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_departments">
                <AdminDepartmentsPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'admin/users',
        lazy: async () => {
          const { default: GlobalUsersPage } = await import('./features/admin/GlobalUsersPage')
          return {
            Component: () => (
              <RequireCapability capability="manage_global">
                <GlobalUsersPage />
              </RequireCapability>
            ),
          }
        },
      },
      {
        path: 'admin/audit',
        lazy: async () => {
          const { default: AuditLogPage } = await import('./features/admin/AuditLogPage')
          return {
            Component: () => (
              <RequireCapability capability="view_audit">
                <AuditLogPage />
              </RequireCapability>
            ),
          }
        },
      },
      { path: '*', element: <NotFound /> },
    ],
  },
])
